import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import { ApiError, myFlowsApi, type MyFlow } from '../services/api/client';

/**
 * Meus Fluxos — área pessoal de rascunho (PRD seção 20).
 *
 * Fora de turmas, atividades e grupos. Ninguém mais vê, nem o professor. Não
 * entra em exportação nem em backup acadêmico. É o caderno de rascunho do
 * aluno: serve para treinar sem estar sendo observado.
 */
export default function MyFlowsPage() {
  const [flows, setFlows] = useState<MyFlow[] | null>(null);
  const [error, setError] = useState('');
  /** PRD 34.5: sem matrícula em turma ativa com a chave ligada (e não é
   * professor), a área inteira fica indisponível — não é um erro comum. */
  const [blocked, setBlocked] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await myFlowsApi.list();
      setFlows(result.flows);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setBlocked(true);
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Falha ao carregar.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (blocked) {
    return (
      <>
        <div className="section-head">
          <div>
            <h2>Meus Fluxos</h2>
          </div>
        </div>

        <section className="card empty-state">
          <p>
            Meus Fluxos não está disponível para você no momento. Essa área só abre para quem
            está matriculado numa turma em que o professor a habilitou.
          </p>
        </section>
      </>
    );
  }

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setError('');
    setCreating(true);

    try {
      await myFlowsApi.create(name);
      setName('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao criar o fluxo.');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(flow: MyFlow) {
    if (!window.confirm(`Excluir "${flow.name}"? Não há como desfazer.`)) return;

    try {
      await myFlowsApi.remove(flow.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao excluir.');
    }
  }

  return (
    <>
      <div className="section-head">
        <div>
          <h2>Meus Fluxos</h2>
          <p className="muted">
            Seu espaço para treinar. Nada aqui faz parte das atividades da
            disciplina, e ninguém mais tem acesso.
          </p>
        </div>
      </div>

      <section className="card">
        <form onSubmit={handleCreate} className="inline-add">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome do novo fluxo"
            required
          />
          <button type="submit" className="primary" disabled={creating}>
            {creating ? 'Criando…' : 'Criar fluxo'}
          </button>
        </form>

        {error && <p className="form-error">{error}</p>}
        {!flows && !error && <p className="muted">Carregando…</p>}

        {flows?.length === 0 && (
          <p className="muted">
            Nenhum fluxo ainda. Crie um acima para praticar modelagem BPMN à
            vontade.
          </p>
        )}

        {flows && flows.length > 0 && (
          <ul className="simple-list">
            {flows.map((flow) => (
              <li key={flow.id}>
                <Link to={`/meus-fluxos/${flow.id}`}>{flow.name}</Link>
                <span className="flow-meta">
                  <span className="muted small">
                    {new Date(flow.updatedAt).toLocaleString('pt-BR', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </span>
                  <button
                    type="button"
                    className="link-danger"
                    onClick={() => void handleDelete(flow)}
                  >
                    Excluir
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
